import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, FormHelperText, Typography } from "@mui/material";

import { StyledMenuItemContainer, StyledMenuListItemText, HtmlTooltipDisabled } from "../StyledComponents/styled";
import SelectFieldShell from "./SelectFieldShell";
import useSelectDropdown from "./useSelectDropdown";
import { DISABLED_OPTION_COLOR, SELECT_COLOR, SELECTED_OPTION_COLOR } from "./selectConstants";
import {
  estimateHierarchyContentWidth,
  filterCompanyHierarchyBySearch,
  paddingLeftForDepth,
} from "./companyHierarchySelectUtils";

/**
 * Hierarchical company single-select (root → national → branch → store).
 * Consumes the same retailers tree as CompanyHierarchyMultiSelect.
 * The full tree is always visible (no expand/collapse). Parents are bold.
 * Any level is selectable; row click selects and closes (keeps search).
 *
 * Optional Users-tab extras: hide search, pin "Add to new company" on top, auto-open.
 * Nodes with selectable === false are grouping rows only.
 * Callers may also pass disabledOptions (same idea as SingleSelect) to grey a row
 * and show a tooltip without hiding it. Brand add/edit uses that for remaining
 * ancestor headers after it prunes companies with nothing shareable.
 */
const CompanyHierarchySingleSelect = ({
  label = "Owner*",
  retailers = [],
  value = null,
  onChange,
  fieldError = "",
  enableFullWidth = true,
  showSearch = true,
  leadingOption = null,
  autoOpen = false,
  disabledOptions,
  tooltipTitleForDisabledOptions,
  // Stable id for screens that tests or labels address. Otherwise each mount gets its own.
  elemId: elemIdOverride,
}) => {
  const [instanceId] = useState(
    () => `company-hierarchy-single-${Math.random().toString(36).substr(2, 9)}`
  );

  const dropdown = useSelectDropdown({ instanceId });
  const {
    open,
    setOpen,
    searchTerm,
    isFocused,
    close: closeDropdown,
  } = dropdown;

  // Escape/click-away clear the search; selection keeps it (close(false)).
  const close = useCallback(
    (clearSearch = true) => closeDropdown({ clearSearch }),
    [closeDropdown]
  );

  const selectedOption = value && typeof value.id !== "undefined" ? value : null;
  const shrink = isFocused || open || selectedOption !== null;

  useEffect(() => {
    if (autoOpen === true) {
      setOpen(true);
    }
  }, [autoOpen, setOpen]);

  const isSelected = useCallback(
    (companyId) => selectedOption !== null && String(selectedOption.id) === String(companyId),
    [selectedOption]
  );

  const isNodeDisabled = useCallback(
    (node) => typeof disabledOptions === "function" && disabledOptions(node) === true,
    [disabledOptions]
  );

  const handleSelect = (node) => {
    // Grouping rows (unassigned ancestors on Update) are not pickable
    if (node.selectable === false) {
      return;
    }
    // Same skip as SingleSelect: disabled rows stay visible but cannot be chosen
    if (isNodeDisabled(node) === true) {
      return;
    }
    if (typeof onChange === "function") {
      onChange({
        id: node.id,
        label: node.label,
        company_level: node.level,
        type: node.type,
      });
    }
    close(false);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    if (typeof onChange === "function") {
      onChange(null);
    }
  };

  const searchValue = searchTerm.trim().toLowerCase();
  const filteredRetailers = useMemo(
    () => filterCompanyHierarchyBySearch(retailers, searchValue),
    [retailers, searchValue]
  );

  // Reported to the shell, which decides the final panel width. Measured on the
  // full tree, not the filtered one, so the panel does not resize while typing.
  const dropdownContentWidth = useMemo(
    () => estimateHierarchyContentWidth(retailers),
    [retailers]
  );

  const renderNode = (node, depth = 0) => {
    const children = Array.isArray(node.children) ? node.children : [];
    const hasChildren = children.length > 0;
    // isHierarchyParent covers a parent whose children were all removed from the list.
    const isParentRow = hasChildren === true || node.isHierarchyParent === true;
    const selected = isSelected(node.id);
    const isGroupingRow = node.selectable === false;
    const isDisabled = isNodeDisabled(node);
    // Grouping rows keep today's look (cursor only). Grey + tooltip is only for
    // disabledOptions, so an assigned parent is not shown as "unavailable".
    const cannotSelect = isGroupingRow === true || isDisabled === true;

    const menuItem = (
      <StyledMenuItemContainer
        className="selectorOptions"
        onClick={() => handleSelect(node)}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: cannotSelect === true ? "default" : "pointer",
          opacity: isDisabled === true ? 0.5 : 1,
          // Disabled rows get a light grey fill so they read as unavailable, not just faded.
          backgroundColor:
            isDisabled === true
              ? DISABLED_OPTION_COLOR
              : selected === true
                ? SELECTED_OPTION_COLOR
                : "transparent",
          // padding / hover must be set through this selector, otherwise the styled MenuItem rule wins
          "&.MuiMenuItem-root": {
            marginRight: 0,
            paddingLeft: paddingLeftForDepth(depth),
            ...(isDisabled === true
              ? {
                  backgroundColor: DISABLED_OPTION_COLOR,
                  "&:hover": { backgroundColor: DISABLED_OPTION_COLOR },
                }
              : {}),
          },
        }}
      >
        <StyledMenuListItemText
          primary={node.label}
          primaryTypographyProps={{
            sx: {
              color: selected === true ? SELECT_COLOR : "inherit",
              // Parents stay bold so the higher level is obvious without expand/collapse
              fontWeight: isParentRow === true ? 700 : selected === true ? 500 : 400,
            },
          }}
        />
      </StyledMenuItemContainer>
    );

    // Tooltip needs a DOM child that can receive hover; wrap only this row so
    // children of a disabled parent stay independently hoverable/selectable.
    const row =
      isDisabled === true ? (
        <HtmlTooltipDisabled
          title={
            typeof tooltipTitleForDisabledOptions === "function"
              ? tooltipTitleForDisabledOptions(node)
              : tooltipTitleForDisabledOptions ?? "This option is unavailable"
          }
          arrow
        >
          <span style={{ display: "block", width: "100%" }}>{menuItem}</span>
        </HtmlTooltipDisabled>
      ) : (
        menuItem
      );

    return (
      <Box key={String(node.id)} sx={{ width: "100%" }}>
        {row}
        {hasChildren === true && children.map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  };

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={typeof elemIdOverride === "string" && elemIdOverride !== "" ? elemIdOverride : instanceId}
      label={label}
      shrink={shrink}
      enableFullWidth={enableFullWidth}
      showSearch={showSearch}
      canClear={selectedOption !== null}
      onClear={handleClear}
      errorContent={
        fieldError ? (
          <FormHelperText sx={{ color: SELECT_COLOR, mt: 0.5 }}>{fieldError}</FormHelperText>
        ) : null
      }
      onAnchorClick={() => setOpen((previous) => !previous)}
      onAnchorKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") {
          close();
        }
      }}
      contentWidth={dropdownContentWidth}
      valueDisplay={
        <Typography
          variant="body2"
          sx={{
            color: SELECT_COLOR,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {selectedOption !== null ? selectedOption.label : ""}
        </Typography>
      }
    >
      {leadingOption !== null && (
        <>
          <StyledMenuItemContainer
            onClick={() => handleSelect(leadingOption)}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: isSelected(leadingOption.id) === true ? SELECTED_OPTION_COLOR : "transparent",
            }}
          >
            <StyledMenuListItemText
              primary={leadingOption.label}
              primaryTypographyProps={{
                sx: {
                  color: isSelected(leadingOption.id) === true ? SELECT_COLOR : "inherit",
                  fontWeight: 500,
                },
              }}
            />
          </StyledMenuItemContainer>
          <Divider />
        </>
      )}
      {filteredRetailers.length === 0 ? (
        <Box sx={{ px: 2, py: 1.5, color: "#62626E", fontSize: 14 }}>No companies found</Box>
      ) : (
        filteredRetailers.map((nationalNode) => renderNode(nationalNode))
      )}
    </SelectFieldShell>
  );
};

export default CompanyHierarchySingleSelect;
